import React from 'react'
import { Link } from 'react-router-dom'
import { 
  Send, Loader, Bot, User, MessageCircle, Lightbulb, 
  TrendingUp, Target, Sparkles, Brain, Compass, FileText,
  Clock, Star, ArrowUp, RefreshCw, BookOpen, Zap, Users,
  ArrowRight
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useJourneyStore } from '../store/journeyStore'
import { useAuthStore } from '../store/authStore'
import toast from 'react-hot-toast'
import ReactMarkdown from 'react-markdown'

const ConsultantPage = () => {
  const [question, setQuestion] = React.useState('')
  const [consultationHistory, setConsultationHistory] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [selectedSuggestion, setSelectedSuggestion] = React.useState(null)
  const messagesEndRef = React.useRef(null)
  
  const { anchors, askConsultant } = useJourneyStore()
  const { user } = useAuthStore()

  // Auto-scroll to bottom when new messages arrive
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [consultationHistory])

  // Quick question suggestions based on completed anchors
  const suggestionCategories = [
    {
      category: 'Strategy Implementation',
      icon: Target,
      color: 'red',
      suggestions: [
        'How do I implement my truth anchor in day-to-day operations?',
        'What are the first three strategic moves I should make?',
        'How do I prioritize between different strategic initiatives?',
        'What metrics should I track to measure truth-anchor alignment?'
      ]
    },
    {
      category: 'Brand Expression',
      icon: Sparkles, 
      color: 'blue',
      suggestions: [
        'How do I ensure consistent brand expression across all touchpoints?',
        'What content themes best serve my truth anchor?',
        'How do I train my team to embody our brand character?',
        'What visual elements should I prioritize for brand recognition?'
      ]
    },
    {
      category: 'Go-to-Market',
      icon: TrendingUp,
      color: 'green',
      suggestions: [
        'Which market segment should I target first?',
        'How do I scale while maintaining truth-anchor integrity?',
        'What channels align best with my brand philosophy?',
        'How do I compete against feature-focused competitors?'
      ]
    },
    {
      category: 'Leadership & Culture',
      icon: Users,
      color: 'purple',
      suggestions: [
        'How do I build a team that embodies our truth anchor?',
        'What hiring criteria align with our brand philosophy?',
        'How do I communicate our strategic anchors to investors?',
        'What culture practices reinforce our brand truth?'
      ]
    }
  ]

  const handleSendQuestion = async (e) => {
    e.preventDefault()
    if (!question.trim() || isLoading) return

    const userQuestion = question.trim()
    setQuestion('')
    setIsLoading(true)

    // Add user question to history
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: userQuestion,
      timestamp: new Date().toISOString()
    }
    setConsultationHistory(prev => [...prev, userMessage])

    try {
      const result = await askConsultant(userQuestion)
      
      if (result.success) {
        const consultantMessage = {
          id: Date.now() + 1,
          role: 'consultant',
          content: result.response,
          confidence: result.confidence,
          timestamp: new Date().toISOString()
        }
        setConsultationHistory(prev => [...prev, consultantMessage])
      } else {
        toast.error(result.error)
      }
    } catch (error) {
      toast.error('Failed to get consultant response. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSuggestionClick = (suggestion) => {
    setQuestion(suggestion)
    setSelectedSuggestion(null)
  }

  const completedAnchors = Object.values(anchors).filter(anchor => 
    anchor && anchor.completed
  ).length

  if (completedAnchors < 4) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center p-4">
        <motion.div 
          className="max-w-2xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="card space-y-6">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <MessageCircle className="w-10 h-10 text-white" />
            </div>
            
            <h1 className="text-3xl font-bold gradient-text">
              Strategic Consultant Mode
            </h1>
            
            <p className="text-lg text-secondary-600">
              Complete your Four Anchor journey to unlock ongoing strategic guidance 
              from your AI consultant. Get personalized advice based on your unique 
              brand foundation.
            </p>
            
            <div className="bg-secondary-50 rounded-lg p-6">
              <h3 className="font-semibold text-secondary-900 mb-3">Complete these anchors to unlock:</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className={`flex items-center space-x-2 ${anchors.truth?.completed ? 'text-green-600' : 'text-secondary-600'}`}>
                  <Target className="w-5 h-5" />
                  <span className="text-sm">Truth Anchor</span>
                </div>
                <div className={`flex items-center space-x-2 ${anchors.expression?.completed ? 'text-green-600' : 'text-secondary-600'}`}>
                  <Sparkles className="w-5 h-5" />
                  <span className="text-sm">Expression Anchor</span>
                </div>
                <div className={`flex items-center space-x-2 ${anchors.attention?.completed ? 'text-green-600' : 'text-secondary-600'}`}>
                  <Brain className="w-5 h-5" />
                  <span className="text-sm">Attention Anchor</span>
                </div>
                <div className={`flex items-center space-x-2 ${anchors.evolution?.completed ? 'text-green-600' : 'text-secondary-600'}`}>
                  <Compass className="w-5 h-5" />
                  <span className="text-sm">Evolution Anchor</span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/journey" className="btn-primary">
                Continue Journey
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/anchors" className="btn-secondary">
                View Progress
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-secondary-200 p-4">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="flex items-center justify-between"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-secondary-900">Strategic Consultant</h1>
                <p className="text-sm text-secondary-600">
                  AI-powered guidance based on your Four Anchor foundation
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm font-medium text-secondary-900">{user?.company_name}</p>
                <p className="text-xs text-secondary-600">All anchors complete</p>
              </div>
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex max-w-7xl mx-auto w-full">
        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto chat-container p-4">
            {consultationHistory.length === 0 ? (
              <motion.div 
                className="text-center py-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Bot className="w-16 h-16 text-purple-600 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                  Welcome to Strategic Consultant Mode
                </h2>
                <p className="text-lg text-secondary-600 mb-8 max-w-2xl mx-auto">
                  I have deep knowledge of your Four Anchor foundation and can provide 
                  strategic guidance for implementation, scaling, and ongoing brand development.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
                  <div className="card text-left">
                    <Lightbulb className="w-8 h-8 text-amber-600 mb-3" />
                    <h3 className="font-semibold text-secondary-900 mb-2">Strategic Insights</h3>
                    <p className="text-sm text-secondary-600">
                      Get personalized recommendations based on your unique truth anchor and brand philosophy.
                    </p>
                  </div>
                  
                  <div className="card text-left">
                    <TrendingUp className="w-8 h-8 text-green-600 mb-3" />
                    <h3 className="font-semibold text-secondary-900 mb-2">Implementation Guidance</h3>
                    <p className="text-sm text-secondary-600">
                      Receive tactical advice for executing your evolution anchor and scaling strategies.
                    </p>
                  </div>
                  
                  <div className="card text-left">
                    <Zap className="w-8 h-8 text-blue-600 mb-3" />
                    <h3 className="font-semibold text-secondary-900 mb-2">Real-time Problem Solving</h3>
                    <p className="text-sm text-secondary-600">
                      Ask specific questions about challenges you're facing in your brand journey.
                    </p>
                  </div>
                  
                  <div className="card text-left">
                    <BookOpen className="w-8 h-8 text-purple-600 mb-3" />
                    <h3 className="font-semibold text-secondary-900 mb-2">Context-Aware Advice</h3>
                    <p className="text-sm text-secondary-600">
                      Every response considers your complete anchor foundation for relevant guidance.
                    </p>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="space-y-6">
                <AnimatePresence>
                  {consultationHistory.map((msg, index) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-3xl w-full ${
                        msg.role === 'user' ? 'chat-user' : 'chat-ai'
                      }`}>
                        <div className="flex items-start space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            msg.role === 'user' 
                              ? 'bg-primary-500 text-white' 
                              : 'bg-purple-100 text-purple-600'
                          }`}>
                            {msg.role === 'user' ? (
                              <User className="w-4 h-4" />
                            ) : (
                              <Bot className="w-4 h-4" />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-medium text-secondary-800">
                                {msg.role === 'user' ? 'You' : 'Strategic Consultant'}
                              </span>
                              <span className="text-xs text-secondary-500">
                                {new Date(msg.timestamp).toLocaleTimeString()}
                              </span>
                              {msg.confidence && (
                                <div className="flex items-center space-x-1">
                                  <Star className="w-3 h-3 text-amber-500" />
                                  <span className="text-xs text-amber-600">
                                    {Math.round(msg.confidence * 100)}% confidence
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            <div className="prose prose-sm max-w-none text-secondary-700">
                              <ReactMarkdown>{msg.content}</ReactMarkdown>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Typing Indicator */}
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start"
                  >
                    <div className="chat-ai max-w-3xl">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                          <Bot className="w-4 h-4" />
                        </div>
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Message Input */}
          <div className="border-t border-secondary-200 p-4">
            <form onSubmit={handleSendQuestion} className="flex space-x-4">
              <div className="flex-1">
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSendQuestion(e)
                    }
                  }}
                  placeholder="Ask about strategy implementation, scaling challenges, competitive positioning, team building..."
                  className="w-full input-field resize-none"
                  rows={3}
                  disabled={isLoading}
                />
              </div>
              <button
                type="submit"
                disabled={!question.trim() || isLoading}
                className="btn-primary px-6 py-3 flex items-center self-end"
              >
                {isLoading ? (
                  <Loader className="loading-spinner" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </form>
            
            <div className="mt-2 text-xs text-secondary-500 text-center">
              Press Enter to send, Shift+Enter for new line
            </div>
          </div>
        </div>

        {/* Suggestions Sidebar */}
        <div className="w-80 border-l border-secondary-200 bg-secondary-50 p-4 overflow-y-auto">
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-4 flex items-center">
                <Lightbulb className="w-5 h-5 mr-2 text-amber-600" />
                Quick Questions
              </h3>
              
              {suggestionCategories.map((category, categoryIndex) => {
                const Icon = category.icon
                
                return (
                  <div key={category.category} className="mb-6">
                    <button
                      onClick={() => setSelectedSuggestion(
                        selectedSuggestion === categoryIndex ? null : categoryIndex
                      )}
                      className="flex items-center space-x-2 text-sm font-medium text-secondary-700 hover:text-secondary-900 mb-3 w-full text-left"
                    >
                      <Icon className={`w-4 h-4 text-${category.color}-600`} />
                      <span>{category.category}</span>
                      <ArrowUp className={`w-3 h-3 transition-transform ${
                        selectedSuggestion === categoryIndex ? 'rotate-180' : ''
                      }`} />
                    </button>
                    
                    <AnimatePresence>
                      {selectedSuggestion === categoryIndex && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="space-y-2 overflow-hidden"
                        >
                          {category.suggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              onClick={() => handleSuggestionClick(suggestion)}
                              className="block w-full text-left text-xs text-secondary-600 hover:text-secondary-800 hover:bg-white p-2 rounded transition-colors"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )
              })}
            </div>

            {/* Quick Actions */}
            <div className="pt-6 border-t border-secondary-200">
              <h4 className="text-sm font-medium text-secondary-700 mb-3">Quick Actions</h4>
              <div className="space-y-2">
                <button
                  onClick={() => setConsultationHistory([])}
                  className="flex items-center space-x-2 text-xs text-secondary-600 hover:text-secondary-800 w-full text-left p-2 hover:bg-white rounded transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>Clear History</span>
                </button>
                
                <Link 
                  to="/anchors"
                  className="flex items-center space-x-2 text-xs text-secondary-600 hover:text-secondary-800 w-full text-left p-2 hover:bg-white rounded transition-colors"
                >
                  <FileText className="w-3 h-3" />
                  <span>Review Anchors</span>
                </Link>
                
                <Link 
                  to="/documents"
                  className="flex items-center space-x-2 text-xs text-secondary-600 hover:text-secondary-800 w-full text-left p-2 hover:bg-white rounded transition-colors"
                >
                  <FileText className="w-3 h-3" />
                  <span>Download PDFs</span>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default ConsultantPage
